<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap"><i class="fe fe-layers" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <h4 class="mb-0 font-weight-bold">Plans</h4>
              <small style="opacity: .8;">Your agency's programmes, their ID card codes, and whether new enrolment is open</small>
            </div>
            <button v-if="canManage" type="button" class="btn btn-light btn-sm font-weight-bold px-4" @click="openForm()">
              <i class="fe fe-plus mr-1"></i> Add plan
            </button>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Add / edit ── -->
        <div v-if="form.open" class="card form-card shadow mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe" :class="form.id ? 'fe-edit-2' : 'fe-plus'" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white">{{ form.id ? 'Edit plan' : 'Add plan' }}</h6>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="save">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="plan-title" class="small font-weight-bold text-secondary">Plan name <span class="text-danger">*</span></label>
                  <input id="plan-title" type="text" required maxlength="191" class="form-control" v-model.trim="form.title"
                    :class="{ 'is-invalid': errors.title }" placeholder="e.g. State Civil Servant" />
                  <div v-if="errors.title" class="invalid-feedback">{{ errors.title[0] }}</div>
                  <small v-else class="form-text text-muted">Exactly the programme name chosen when enrolling (it is matched to enrollees by name).</small>
                </div>
                <div class="form-group col-md-3">
                  <label for="plan-code" class="small font-weight-bold text-secondary">ID card code</label>
                  <input id="plan-code" type="text" maxlength="191" class="form-control text-uppercase" v-model.trim="form.id_card_code"
                    :class="{ 'is-invalid': errors.id_card_code }" placeholder="e.g. SCS" />
                  <div v-if="errors.id_card_code" class="invalid-feedback">{{ errors.id_card_code[0] }}</div>
                </div>
                <div class="form-group col-md-3 d-flex align-items-end">
                  <div class="custom-control custom-checkbox mb-2">
                    <input id="plan-lga" type="checkbox" class="custom-control-input" v-model="form.id_card_code_with_lga" />
                    <label for="plan-lga" class="custom-control-label small">Code is followed by the LGA</label>
                  </div>
                </div>
                <div class="form-group col-md-3">
                  <label for="plan-cost" class="small font-weight-bold text-secondary">Cost (₦)</label>
                  <input id="plan-cost" type="number" min="0" step="0.01" class="form-control" v-model="form.cost" />
                </div>
                <div class="form-group col-md-3">
                  <label for="plan-interval" class="small font-weight-bold text-secondary">Interval</label>
                  <select id="plan-interval" class="form-control" v-model="form.plan_inteval">
                    <option v-for="i in intervals" :key="i" :value="i">{{ i }}</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label for="plan-description" class="small font-weight-bold text-secondary">Description</label>
                  <input id="plan-description" type="text" maxlength="5000" class="form-control" v-model.trim="form.description" />
                </div>
              </div>
              <p v-if="form.id_card_code" class="small text-muted mb-3">
                <i class="fe fe-credit-card mr-1"></i> ID numbers on this plan look like
                <span class="code-chip">…/{{ form.id_card_code.toUpperCase() }}{{ form.id_card_code_with_lga ? '/LGA' : '' }}/…</span>.
                New numbers are still made by the built-in rules; this code mirrors them.
              </p>
              <div class="d-flex justify-content-end" style="gap: 10px;">
                <button type="button" class="btn btn-outline-secondary px-4" @click="form.open = false">Cancel</button>
                <button type="submit" class="btn btn-submit px-5" :disabled="saving"><i class="fe fe-save mr-2"></i> {{ saving ? 'Saving…' : 'Save plan' }}</button>
              </div>
            </form>
          </div>
        </div>

        <!-- ── Plans ── -->
        <div class="card table-card shadow-sm mb-4">
          <div class="d-flex flex-wrap align-items-center justify-content-between px-4 pt-4 pb-3" style="gap: 8px;">
            <div>
              <h6 class="mb-0 font-weight-bold"><i class="fe fe-list text-primary mr-2"></i> Plans</h6>
              <small class="text-muted">{{ plans.length }} plan(s){{ pausedCount ? ` · ${pausedCount} paused` : '' }}</small>
            </div>
            <input type="search" class="form-control form-control-sm" style="max-width: 240px;" v-model.trim="search"
              aria-label="Search plans" placeholder="Search name or code…" />
          </div>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th px-4">Plan</th>
                  <th class="table-th">ID card code</th>
                  <th class="table-th text-right">Enrollees</th>
                  <th class="table-th">Enrolment</th>
                  <th v-if="canManage" class="table-th"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading"><td colspan="5" class="text-center text-muted py-5">Loading plans…</td></tr>
                <template v-else>
                  <tr v-for="p in shownPlans" :key="p.id" class="table-row">
                    <td class="px-4 py-3">
                      <p class="mb-0 font-weight-bold" style="font-size: .9rem;">{{ p.title }}</p>
                      <small class="text-muted">{{ p.cost ? money(p.cost) + ' · ' : '' }}{{ p.plan_inteval }}</small>
                    </td>
                    <td class="py-3">
                      <span v-if="p.id_card_code" class="code-chip">{{ p.id_card_code }}</span><span v-else class="text-muted">—</span>
                      <span v-if="p.id_card_code && p.id_card_code_with_lga" class="rel-chip ml-1">+ LGA</span>
                    </td>
                    <td class="py-3 text-right">
                      <span class="font-weight-bold">{{ num(p.principals + p.dependants) }}</span>
                      <small class="d-block text-muted">{{ num(p.principals) }} principals · {{ num(p.dependants) }} dependants</small>
                    </td>
                    <td class="py-3">
                      <span v-if="p.enrollment_halted" class="status-badge status-badge--rejected" :title="haltTitle(p)">
                        <i class="fe fe-pause-circle mr-1"></i> Paused
                      </span>
                      <span v-else class="status-badge status-badge--approved"><i class="fe fe-check-circle mr-1"></i> Open</span>
                      <small v-if="p.enrollment_halted" class="d-block text-muted mt-1">
                        {{ p.enrollment_halted_by || 'An admin' }}, {{ date(p.enrollment_halted_at) }}<template v-if="p.enrollment_halt_reason"> · {{ p.enrollment_halt_reason }}</template>
                      </small>
                    </td>
                    <td v-if="canManage" class="py-3 pr-4">
                      <div class="d-flex flex-wrap justify-content-end" style="gap: 6px;">
                        <button v-if="!p.enrollment_halted" type="button" class="btn btn-sm btn-warning" @click="setHalt(p, true)">
                          <i class="fe fe-pause-circle mr-1"></i> Pause enrolment
                        </button>
                        <button v-else type="button" class="btn btn-sm btn-success" @click="setHalt(p, false)">
                          <i class="fe fe-play-circle mr-1"></i> Resume
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary" :aria-label="'Edit ' + p.title" @click="openForm(p)"><i class="fe fe-edit-2"></i></button>
                        <button v-if="!p.principals" type="button" class="btn btn-sm btn-outline-danger" :aria-label="'Delete ' + p.title" @click="remove(p)"><i class="fe fe-trash-2"></i></button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!shownPlans.length">
                    <td colspan="5" class="text-center text-muted py-5">
                      <i class="fe fe-layers d-block mb-2" style="font-size: 2rem;"></i>
                      {{ search ? 'No plan matches your search' : 'No plans yet' }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Programmes without a plan ── -->
        <div v-if="withoutPlan.length" class="card table-card shadow-sm">
          <div class="px-4 pt-4 pb-3">
            <h6 class="mb-0 font-weight-bold"><i class="fe fe-alert-circle text-warning mr-2"></i> Programmes without a plan</h6>
            <small class="text-muted">Enrollees are on these programmes, but there is no plan with that name, so enrolment into them cannot be paused yet.</small>
          </div>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head"><tr><th class="table-th px-4">Programme</th><th class="table-th text-right">Principals</th><th v-if="canManage" class="table-th"></th></tr></thead>
              <tbody>
                <tr v-for="w in withoutPlan" :key="w.name" class="table-row">
                  <td class="px-4 py-2">{{ w.name }}</td>
                  <td class="py-2 text-right">{{ num(w.principals) }}</td>
                  <td v-if="canManage" class="py-2 pr-4 text-right">
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="openForm({ title: w.name })"><i class="fe fe-plus mr-1"></i> Add as plan</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

const EMPTY = { open: false, id: null, title: "", original_title: "", id_card_code: "", id_card_code_with_lga: false, cost: "", plan_inteval: "Yearly", description: "", principals: 0 };

/** The agency's plans (GET/POST/PUT/DELETE agency-plans, POST agency-plans/{id}/enrollment-halt). */
export default {
  name: "ManagePlans",
  components: { Navbar },
  data() {
    return {
      plans: [],
      withoutPlan: [],
      canManage: false,
      loading: true,
      saving: false,
      search: "",
      errors: {},
      form: { ...EMPTY },
      intervals: ["Yearly", "Monthly", "Weekly", "Daily"],
    };
  },
  computed: {
    shownPlans() {
      const q = this.search.toLowerCase();
      return q ? this.plans.filter((p) => [p.title, p.id_card_code].some((v) => String(v || "").toLowerCase().includes(q))) : this.plans;
    },
    pausedCount() {
      return this.plans.filter((p) => p.enrollment_halted).length;
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/agency-plans")
        .then((res) => {
          this.plans = res.data.data.plans;
          this.withoutPlan = res.data.data.programmes_without_plan || [];
          this.canManage = !!res.data.data.can_manage;
        })
        .catch((error) => this.fail(error, "Could not load the plans."))
        .finally(() => { this.loading = false; });
    },
    openForm(plan) {
      this.errors = {};
      const p = plan || {};
      this.form = {
        ...EMPTY,
        open: true,
        id: p.id || null,
        title: p.title || "",
        original_title: p.title || "",
        id_card_code: p.id_card_code || "",
        id_card_code_with_lga: !!p.id_card_code_with_lga,
        cost: p.cost || "",
        plan_inteval: p.plan_inteval || "Yearly",
        description: p.description && p.description !== p.title ? p.description : "",
        principals: p.principals || 0,
      };
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    save() {
      const f = this.form;
      if (f.id && f.principals && f.title.toLowerCase() !== f.original_title.toLowerCase()
        && !confirm(`${this.num(f.principals)} enrollee(s) are on "${f.original_title}". They keep that name, so they will no longer match this plan after the rename. Rename anyway?`)) {
        return;
      }
      this.saving = true;
      this.errors = {};
      const body = {
        title: f.title, id_card_code: f.id_card_code || null, id_card_code_with_lga: f.id_card_code_with_lga,
        cost: f.cost === "" ? null : f.cost, plan_inteval: f.plan_inteval, description: f.description || null,
      };
      const req = f.id ? this.axios.put(`/api/v1/auth/agency-plans/${f.id}`, body) : this.axios.post("/api/v1/auth/agency-plans", body);
      req
        .then((res) => {
          this.$toasted.success(res.data.message || "Plan saved", { position: "top-center", duration: 3000 });
          this.form = { ...EMPTY };
          this.load();
        })
        .catch((error) => {
          this.errors = (error.response && error.response.data && error.response.data.errors) || {};
          this.fail(error, "Could not save the plan.");
        })
        .finally(() => { this.saving = false; });
    },
    setHalt(plan, halted) {
      let reason = null;
      if (halted) {
        reason = window.prompt(`Pause enrolment into "${plan.title}"?\nNobody will be able to enrol a principal into it or add dependants to its enrollees until it is resumed.\n\nReason (optional):`, "");
        if (reason === null) return;
      } else if (!confirm(`Open enrolment into "${plan.title}" again?`)) {
        return;
      }
      this.axios
        .post(`/api/v1/auth/agency-plans/${plan.id}/enrollment-halt`, { halted, reason: reason || null })
        .then((res) => {
          this.$toasted.success(res.data.message, { position: "top-center", duration: 3000 });
          this.load();
        })
        .catch((error) => this.fail(error, "Could not change the plan."));
    },
    remove(plan) {
      if (!confirm(`Delete the plan "${plan.title}"?`)) return;
      this.axios
        .delete(`/api/v1/auth/agency-plans/${plan.id}`)
        .then(() => {
          this.$toasted.success("Plan deleted", { position: "top-center", duration: 3000 });
          this.load();
        })
        .catch((error) => this.fail(error, "Could not delete the plan."));
    },
    fail(error, fallback) {
      const data = (error.response && error.response.data) || {};
      this.$toasted.error(data.message || fallback, { position: "top-center", duration: 5000 });
    },
    haltTitle(p) {
      return `Paused by ${p.enrollment_halted_by || "an admin"} on ${this.date(p.enrollment_halted_at)}${p.enrollment_halt_reason ? ": " + p.enrollment_halt_reason : ""}`;
    },
    num(v) {
      return Number(v || 0).toLocaleString();
    },
    money(v) {
      return "₦" + Number(v || 0).toLocaleString();
    },
    date(v) {
      if (!v) return "";
      const d = new Date(String(v).replace(" ", "T"));
      return isNaN(d) ? v : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>
